import { useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useTheme } from "../../../context/ThemeContext";
import { createSalaryChange, deleteSalaryChange, detectSalaryChanges, fetchSalaryHistory, updateSalaryChange } from "../services/payrollService";
import { fetchTeamMembers } from "../../team/services/teamService";
import { PAY_FREQUENCY_OPTIONS, payFrequencyLabel } from "../../team/theme";
import { buildThemeClasses, formatMoney } from "../theme";
import type { SalaryChange, SalaryDetection } from "../types";

// ---------------------------------------------------------------------------
// Historial de sueldos (#1487).
//
// El padrón guarda el sueldo ACTUAL; aquí se ve cuánto ganaba cada persona y
// desde cuándo. Se alimenta solo (alta y edición de la ficha, rangos) y quien
// administra puede registrar a mano los aumentos que ya pasaron, corregirlos o
// borrarlos. Movimientos extraordinarios compara cada pago contra la entrada
// vigente en su fecha, así que documentar un aumento saca de la lista los
// pagos anteriores por el sueldo de entonces.
//
// `draft` llega desde un renglón de Movimientos extraordinarios («Registrar
// como aumento de sueldo»): elige a la persona y deja el formulario lleno.
// ---------------------------------------------------------------------------

/**
 * Lo que trae un renglón de movimientos para proponer un aumento. Primero se
 * intenta la detección por los pagos de esa persona; si no propone nada, se
 * abre el formulario: con diferencia negativa (se le pagaba menos que el
 * sueldo actual) el monto del renglón es el sueldo ANTERIOR y el aumento es al
 * sueldo del padrón; con diferencia positiva, el renglón es el sueldo nuevo.
 */
export interface SalaryDraft {
  teamMemberId: string;
  teamMemberName: string;
  effectiveFrom: string;
  amount: number;
  /** Sueldo del padrón para ese pago (`null` si no se pudo comparar). */
  expected: number | null;
  difference: number | null;
  currency: string | null;
}

interface MemberOption {
  id: string;
  name: string;
  fixedSalary?: number | null;
  currency?: string | null;
  payFrequency?: string | null;
}

interface Props {
  /** Quien administra registra, corrige y borra; los demás solo ven lo suyo. */
  canEdit: boolean;
  draft?: SalaryDraft | null;
  onDraftConsumed?: () => void;
}

interface FormState {
  id: string | null;
  sinceAlways: boolean;
  effectiveFrom: string;
  amount: string;
  previousAmount: string;
  currency: string;
  payFrequency: string;
  reason: string;
  applyToRoster: boolean;
}

const emptyForm = (over: Partial<FormState> = {}): FormState => ({
  id: null,
  sinceAlways: false,
  effectiveFrom: "",
  amount: "",
  previousAmount: "",
  currency: "",
  payFrequency: "semimonthly",
  reason: "",
  applyToRoster: false,
  ...over,
});

export default function SalaryHistoryPanel({ canEdit, draft = null, onDraftConsumed }: Props) {
  const { t } = useTranslation("common");
  const { isDarkMode } = useTheme();
  const tc = buildThemeClasses(isDarkMode);

  const [items, setItems] = useState<SalaryChange[]>([]);
  const [roster, setRoster] = useState<MemberOption[]>([]);
  const [memberId, setMemberId] = useState("");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [form, setForm] = useState<FormState | null>(null);
  const [saving, setSaving] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await fetchSalaryHistory();
      setItems(res.items ?? []);
    } catch {
      setError(t("payroll.salaryHistory.loadError"));
    } finally {
      setLoading(false);
    }
  }, [t]);

  useEffect(() => {
    void load();
  }, [load]);

  // El padrón da los nombres (y el sueldo actual) de quienes aún no tienen
  // historial. Quien no administra no lo puede leer: se queda con lo suyo.
  useEffect(() => {
    let alive = true;
    fetchTeamMembers({ limit: 200 })
      .then((res) => {
        if (!alive) return;
        setRoster(
          (res.items ?? []).map((m) => ({
            id: m.id,
            name: [m.name, m.lastName].filter(Boolean).join(" ") || m.id,
            fixedSalary: m.fixedSalary ?? null,
            currency: m.currency ?? null,
            payFrequency: m.payFrequency ?? null,
          })),
        );
      })
      .catch(() => {
        if (alive) setRoster([]);
      });
    return () => {
      alive = false;
    };
  }, []);

  // Aumentos detectados por los pagos (una persona o todos).
  const [detection, setDetection] = useState<SalaryDetection[] | null>(null);
  const [detecting, setDetecting] = useState(false);

  const runDetect = useCallback(
    async (teamMemberId?: string) => {
      setDetecting(true);
      setError(null);
      setNotice(null);
      try {
        const res = await detectSalaryChanges({ teamMemberId });
        setDetection(res.items ?? []);
        return res.items ?? [];
      } catch {
        setError(t("payroll.salaryHistory.detectError"));
        return null;
      } finally {
        setDetecting(false);
      }
    },
    [t],
  );

  const applyDetected = async (teamMemberId?: string) => {
    setSaving(true);
    setError(null);
    try {
      const res = await detectSalaryChanges({ teamMemberId, apply: true });
      setNotice(t("payroll.salaryHistory.applied", { count: res.applied }));
      setDetection((d) => (d ? d.filter((r) => (teamMemberId ? r.teamMemberId !== teamMemberId : false)) : d));
      await load();
    } catch {
      setError(t("payroll.salaryHistory.saveError"));
    } finally {
      setSaving(false);
    }
  };

  // Un renglón de movimientos propone un aumento: se elige a la persona, se
  // revisan sus pagos y, si no sale nada, se abre el formulario ya orientado.
  useEffect(() => {
    if (!draft) return;
    const current = draft;
    setMemberId(current.teamMemberId);
    setForm(null);
    setNotice(null);
    onDraftConsumed?.();
    void runDetect(current.teamMemberId).then((items) => {
      if (items === null) return;
      const mine = items.find((r) => r.teamMemberId === current.teamMemberId);
      if (mine && mine.status === "proposed") return;
      const lowerThanRoster = current.difference !== null && current.difference < 0 && current.expected !== null;
      setForm(
        emptyForm({
          // Se le pagaba menos que el sueldo actual: el renglón es el sueldo
          // anterior y el aumento es al del padrón. Si no, el renglón es el nuevo.
          effectiveFrom: current.effectiveFrom,
          amount: String(lowerThanRoster ? current.expected : current.amount),
          previousAmount: lowerThanRoster ? String(current.amount) : current.expected == null ? "" : String(current.expected),
          currency: current.currency ?? "",
        }),
      );
    });
  }, [draft, onDraftConsumed, runDetect]);

  const members = useMemo(() => {
    const byId = new Map<string, MemberOption>();
    for (const m of roster) byId.set(m.id, m);
    for (const c of items) if (!byId.has(c.teamMemberId)) byId.set(c.teamMemberId, { id: c.teamMemberId, name: c.teamMemberName || c.teamMemberId });
    if (draft && !byId.has(draft.teamMemberId)) byId.set(draft.teamMemberId, { id: draft.teamMemberId, name: draft.teamMemberName });
    return [...byId.values()].sort((a, b) => a.name.localeCompare(b.name));
  }, [roster, items, draft]);

  // Quien solo ve lo suyo recibe una sola persona: se elige sola.
  useEffect(() => {
    if (!memberId && members.length === 1) setMemberId(members[0].id);
  }, [members, memberId]);

  const member = members.find((m) => m.id === memberId) ?? null;
  const rows = useMemo(() => items.filter((c) => c.teamMemberId === memberId), [items, memberId]);
  const hasHistory = rows.length > 0;

  const openNew = () => {
    setNotice(null);
    setForm(
      emptyForm({
        currency: member?.currency ?? "",
        payFrequency: member?.payFrequency ?? "semimonthly",
        previousAmount: !hasHistory && member?.fixedSalary ? String(member.fixedSalary) : "",
      }),
    );
  };

  const openEdit = (c: SalaryChange) => {
    setNotice(null);
    setForm(
      emptyForm({
        id: c.id,
        sinceAlways: c.effectiveFrom === null,
        effectiveFrom: c.effectiveFrom ?? "",
        amount: String(c.amount),
        currency: c.currency ?? "",
        payFrequency: c.payFrequency ?? "semimonthly",
        reason: c.reason ?? "",
      }),
    );
  };

  const submit = async () => {
    if (!form || !memberId) return;
    const amount = Number(form.amount);
    if (!(amount > 0) || (!form.sinceAlways && !form.effectiveFrom)) return;
    setSaving(true);
    setError(null);
    try {
      const effectiveFrom = form.sinceAlways ? null : form.effectiveFrom;
      if (form.id) {
        await updateSalaryChange(form.id, {
          effectiveFrom,
          amount,
          currency: form.currency || null,
          payFrequency: form.payFrequency || null,
          reason: form.reason.trim() || null,
        });
      } else {
        const res = await createSalaryChange({
          teamMemberId: memberId,
          effectiveFrom,
          amount,
          previousAmount: !hasHistory && form.previousAmount ? Number(form.previousAmount) : undefined,
          currency: form.currency || null,
          payFrequency: form.payFrequency || null,
          reason: form.reason.trim() || null,
          applyToRoster: form.applyToRoster,
        });
        if (res.rosterUpdated) setNotice(t("payroll.salaryHistory.rosterUpdated"));
      }
      setForm(null);
      await load();
    } catch {
      setError(t("payroll.salaryHistory.saveError"));
    } finally {
      setSaving(false);
    }
  };

  const remove = async (id: string) => {
    if (confirmDelete !== id) {
      setConfirmDelete(id);
      return;
    }
    setSaving(true);
    setError(null);
    try {
      await deleteSalaryChange(id);
      setConfirmDelete(null);
      await load();
    } catch {
      setError(t("payroll.salaryHistory.saveError"));
    } finally {
      setSaving(false);
    }
  };

  const inputCls = `rounded-lg border px-2 py-1.5 text-sm ${tc.border} ${tc.cardBg} ${tc.text}`;
  const th = `px-3 py-2 text-left text-xs font-medium ${tc.textSecondary}`;
  const td = `px-3 py-2 text-sm ${tc.text}`;
  const btn = `rounded-md border px-2 py-1 text-xs ${tc.border} ${tc.text} ${tc.buttonHover}`;
  const money = (amount: number | null | undefined, currency?: string | null) => (amount == null ? "—" : formatMoney(amount, currency || undefined));

  return (
    <section className={`mb-4 rounded-xl border p-4 ${tc.cardBorder} ${tc.cardBg}`} aria-labelledby="salary-history-title">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h3 id="salary-history-title" className={`text-base font-semibold ${tc.text}`}>
            {t("payroll.salaryHistory.title")}
          </h3>
          <p className={`mt-0.5 text-xs ${tc.textSecondary}`}>{t("payroll.salaryHistory.subtitle")}</p>
        </div>
        {canEdit && (
          <div className="flex flex-wrap gap-2">
            <button type="button" className={btn} disabled={detecting} onClick={() => void runDetect(memberId || undefined)}>
              <i className="ri-search-eye-line mr-1" aria-hidden="true" />
              {memberId ? t("payroll.salaryHistory.detect") : t("payroll.salaryHistory.detectAll")}
            </button>
            {memberId && !form && (
              <button type="button" className={btn} onClick={openNew}>
                <i className="ri-add-line mr-1" aria-hidden="true" />
                {t("payroll.salaryHistory.add")}
              </button>
            )}
          </div>
        )}
      </div>

      {detecting && <p className={`mt-3 text-sm ${tc.textSecondary}`}>{t("payroll.salaryHistory.detecting")}</p>}
      {detection && !detecting && (
        <div className={`mt-3 rounded-lg border p-3 ${tc.border}`} data-testid="salary-detection">
          <div className="flex flex-wrap items-start justify-between gap-2">
            <div>
              <p className={`text-sm font-semibold ${tc.text}`}>{t("payroll.salaryHistory.proposalsTitle")}</p>
              <p className={`text-xs ${tc.textSecondary}`}>{t("payroll.salaryHistory.proposalsHint")}</p>
            </div>
            {canEdit && detection.filter((r) => r.status === "proposed").length > 1 && (
              <button type="button" className={`${btn} font-medium`} disabled={saving} onClick={() => void applyDetected()}>
                {t("payroll.salaryHistory.applyAll")}
              </button>
            )}
          </div>
          {detection.filter((r) => r.status === "proposed").length === 0 ? (
            <p className={`mt-2 text-sm ${tc.textSecondary}`}>{t("payroll.salaryHistory.noProposals")}</p>
          ) : (
            <ul className="mt-2 space-y-2">
              {detection
                .filter((r) => r.status === "proposed")
                .map((r) => (
                  <li key={r.teamMemberId} className={`rounded-md border p-2 ${tc.border}`} data-testid={`salary-detection-${r.teamMemberId}`}>
                    <div className="flex flex-wrap items-center justify-between gap-2">
                      <span className={`text-sm font-medium ${tc.text}`}>{r.teamMemberName}</span>
                      {canEdit && (
                        <button type="button" className={`${btn} font-medium`} disabled={saving} onClick={() => void applyDetected(r.teamMemberId)}>
                          {t("payroll.salaryHistory.applyOne")}
                        </button>
                      )}
                    </div>
                    <ul className={`mt-1 space-y-0.5 text-xs ${tc.textSecondary}`}>
                      {r.proposals.map((p, i) => (
                        <li key={`${p.effectiveFrom ?? "always"}-${i}`}>
                          {p.source === "initial"
                            ? t("payroll.salaryHistory.proposalInitial", { amount: money(p.amount, p.currency), periods: p.periods, first: p.firstPaidOn })
                            : t("payroll.salaryHistory.proposalChange", { date: p.effectiveFrom, previous: money(p.previousAmount, p.currency), amount: money(p.amount, p.currency), periods: p.periods })}
                        </li>
                      ))}
                    </ul>
                    {r.lastDiffers && <p className="mt-1 text-xs text-amber-600">{t("payroll.salaryHistory.lastDiffers")}</p>}
                  </li>
                ))}
            </ul>
          )}
        </div>
      )}

      <div className="mt-3 flex flex-wrap items-end gap-3">
        <label className={`flex flex-col gap-1 text-xs ${tc.textSecondary}`}>
          {t("payroll.salaryHistory.member")}
          <select className={inputCls} value={memberId} onChange={(e) => { setMemberId(e.target.value); setForm(null); setConfirmDelete(null); }}>
            <option value="">{t("payroll.salaryHistory.pickMember")}</option>
            {members.map((m) => (
              <option key={m.id} value={m.id}>
                {m.name}
              </option>
            ))}
          </select>
        </label>
        {member?.fixedSalary ? (
          <p className={`pb-1.5 text-xs ${tc.textSecondary}`}>
            {t("payroll.salaryHistory.current", { amount: `${money(member.fixedSalary, member.currency)} ${payFrequencyLabel(t, member.payFrequency ?? "semimonthly")}` })}
          </p>
        ) : null}
      </div>

      {error && <p className="mt-3 text-sm text-red-500">{error}</p>}
      {notice && <p className="mt-3 text-sm text-emerald-600">{notice}</p>}

      {form && canEdit && (
        <form
          className={`mt-3 grid gap-3 rounded-lg border p-3 md:grid-cols-3 ${tc.border}`}
          aria-label={form.id ? t("payroll.salaryHistory.edit") : t("payroll.salaryHistory.add")}
          onSubmit={(e) => {
            e.preventDefault();
            void submit();
          }}
        >
          <label className={`flex flex-col gap-1 text-xs ${tc.textSecondary}`}>
            {t("payroll.salaryHistory.effectiveFrom")}
            <input type="date" className={inputCls} value={form.effectiveFrom} disabled={form.sinceAlways} required={!form.sinceAlways} onChange={(e) => setForm({ ...form, effectiveFrom: e.target.value })} />
            <span className="flex items-center gap-1">
              <input type="checkbox" checked={form.sinceAlways} onChange={(e) => setForm({ ...form, sinceAlways: e.target.checked })} />
              {t("payroll.salaryHistory.sinceAlways")}
            </span>
          </label>
          <label className={`flex flex-col gap-1 text-xs ${tc.textSecondary}`}>
            {t("payroll.salaryHistory.amount")}
            <input type="number" min="0.01" step="0.01" className={inputCls} value={form.amount} required onChange={(e) => setForm({ ...form, amount: e.target.value })} />
          </label>
          {!form.id && !hasHistory && (
            <label className={`flex flex-col gap-1 text-xs ${tc.textSecondary}`}>
              {t("payroll.salaryHistory.previousAmount")}
              <input type="number" min="0.01" step="0.01" className={inputCls} value={form.previousAmount} onChange={(e) => setForm({ ...form, previousAmount: e.target.value })} />
              <span>{t("payroll.salaryHistory.previousAmountHint")}</span>
            </label>
          )}
          <label className={`flex flex-col gap-1 text-xs ${tc.textSecondary}`}>
            {t("payroll.salaryHistory.currency")}
            <input type="text" maxLength={3} className={`${inputCls} uppercase`} value={form.currency} placeholder="MXN" onChange={(e) => setForm({ ...form, currency: e.target.value.toUpperCase() })} />
          </label>
          <label className={`flex flex-col gap-1 text-xs ${tc.textSecondary}`}>
            {t("payroll.salaryHistory.payFrequency")}
            <select className={inputCls} value={form.payFrequency} onChange={(e) => setForm({ ...form, payFrequency: e.target.value })}>
              {PAY_FREQUENCY_OPTIONS.map((f) => (
                <option key={f} value={f}>
                  {payFrequencyLabel(t, f)}
                </option>
              ))}
            </select>
          </label>
          <label className={`flex flex-col gap-1 text-xs md:col-span-3 ${tc.textSecondary}`}>
            {t("payroll.salaryHistory.reason")}
            <input type="text" className={inputCls} value={form.reason} placeholder={t("payroll.salaryHistory.reasonPlaceholder")} onChange={(e) => setForm({ ...form, reason: e.target.value })} />
          </label>
          {!form.id && (
            <label className={`flex items-center gap-2 text-xs md:col-span-3 ${tc.textSecondary}`}>
              <input type="checkbox" checked={form.applyToRoster} onChange={(e) => setForm({ ...form, applyToRoster: e.target.checked })} />
              {t("payroll.salaryHistory.applyToRoster")}
              <span className="opacity-70">· {t("payroll.salaryHistory.applyToRosterHint")}</span>
            </label>
          )}
          <div className="flex gap-2 md:col-span-3">
            <button type="submit" className={`${btn} font-medium`} disabled={saving}>
              {t("payroll.salaryHistory.save")}
            </button>
            <button type="button" className={btn} disabled={saving} onClick={() => setForm(null)}>
              {t("payroll.salaryHistory.cancel")}
            </button>
          </div>
        </form>
      )}

      {loading ? (
        <p className={`mt-4 text-sm ${tc.textSecondary}`}>{t("payroll.loading")}</p>
      ) : !memberId ? null : rows.length === 0 ? (
        <p className={`mt-4 text-sm ${tc.textSecondary}`}>{t("payroll.salaryHistory.empty")}</p>
      ) : (
        <div className="mt-3 overflow-x-auto">
          <table className="w-full min-w-[720px] border-collapse">
            <thead>
              <tr className={`border-b ${tc.border}`}>
                <th className={th}>{t("payroll.salaryHistory.effectiveFrom")}</th>
                <th className={`${th} text-right`}>{t("payroll.salaryHistory.amount")}</th>
                <th className={`${th} text-right`}>{t("payroll.salaryHistory.previousAmount")}</th>
                <th className={th}>{t("payroll.salaryHistory.reason")}</th>
                <th className={th}>{t("payroll.salaryHistory.source")}</th>
                <th className={th}>{t("payroll.salaryHistory.registeredBy")}</th>
                {canEdit && <th className={th} />}
              </tr>
            </thead>
            <tbody>
              {rows.map((c) => (
                <tr key={c.id} className={`border-b align-top ${tc.border}`} data-testid={`salary-change-${c.id}`}>
                  <td className={`${td} whitespace-nowrap`}>{c.effectiveFrom ?? t("payroll.salaryHistory.sinceAlways")}</td>
                  <td className={`${td} whitespace-nowrap text-right font-medium`}>
                    {money(c.amount, c.currency)}
                    <span className={`block text-xs font-normal ${tc.textSecondary}`}>{payFrequencyLabel(t, c.payFrequency ?? "semimonthly")}</span>
                  </td>
                  <td className={`${td} whitespace-nowrap text-right`}>{money(c.previousAmount, c.previousCurrency ?? c.currency)}</td>
                  <td className={td}>{c.reason || "—"}</td>
                  <td className={`${td} whitespace-nowrap`}>{t(`payroll.salaryHistory.sources.${c.source}`)}</td>
                  <td className={`${td} whitespace-nowrap`}>
                    {c.createdByName || c.createdBy || "—"}
                    {c.createdAt && <span className={`block text-xs ${tc.textSecondary}`}>{String(c.createdAt).slice(0, 10)}</span>}
                  </td>
                  {canEdit && (
                    <td className={`${td} whitespace-nowrap text-right`}>
                      <button type="button" className={`${btn} mr-1`} disabled={saving} onClick={() => openEdit(c)}>
                        {t("payroll.salaryHistory.edit")}
                      </button>
                      <button type="button" className={`${btn} text-red-500`} disabled={saving} onClick={() => void remove(c.id)}>
                        {confirmDelete === c.id ? t("payroll.salaryHistory.confirmDelete") : t("payroll.salaryHistory.delete")}
                      </button>
                    </td>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </section>
  );
}
